import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCapabilities,
  selectNodeCapabilitiesEntry
} from '../../redux/capabilities/selectors';
import { fetchCapabilities } from '../../redux/capabilities/actions';
import type {
  AccessLevel,
  SelfCapabilities
} from '../../service/capability.service';

/**
 * The caller's own resolved capability row for a workspace, as last fetched by the
 * `fetchUser` thunk (redux/auth/actions.ts) once `default_workspace.id` is known.
 * `undefined` before that fetch resolves, on failure, or if the row on hand was
 * resolved for a different workspace than the one asked for here — a workspace switch
 * reloads the page, so that last case is only ever a brief loading window, never a
 * stale answer.
 */
export const useSelfCapabilities = (
  workspaceId: number | undefined
): SelfCapabilities | undefined => {
  const entry = useSelector((state: any) =>
    workspaceId ? selectNodeCapabilitiesEntry(state, workspaceId) : undefined
  ) as { data: SelfCapabilities | null } | undefined;
  return entry?.data ?? undefined;
};

/**
 * Capabilities for an arbitrary node (organisation, entity or workspace), fetched on
 * first use without becoming the active node. The fetch is keyed on "no entry yet": the
 * LOADING action creates the entry synchronously, so the effect cannot loop while the
 * request is in flight, and a failed fetch leaves an entry rather than retrying forever.
 */
export const useNodeCapabilities = (
  nodeId: number | undefined
): SelfCapabilities | undefined => {
  const dispatch = useDispatch();
  const entry = useSelector((state: any) =>
    nodeId ? selectNodeCapabilitiesEntry(state, nodeId) : undefined
  ) as { data: SelfCapabilities | null } | undefined;
  const missing = nodeId != null && !entry;
  useEffect(() => {
    if (missing) (dispatch as any)(fetchCapabilities(nodeId as number, { 
      active: false 
    }));
  }, [missing, nodeId, dispatch]);
  return entry?.data ?? undefined;
};

export const hasCapability = (
  capabilities: SelfCapabilities | undefined,
  module: string,
  level: AccessLevel
): boolean => capabilities?.capabilities[module] === level;

/**
 * The raw access level the active workspace resolved for `module`, or `undefined` while
 * the row is still loading. Use this over {@link useCapability} when the five levels are
 * not interchangeable — `ASSIGNED_ONLY` and `READ_OWN` narrow the caller to their own
 * records, which is a scoping fact the UI has to state rather than a permission to hide
 * a control over.
 */
export const useAccessLevel = (module: string): AccessLevel | undefined => {
  const capabilities = useSelector(selectCapabilities) as SelfCapabilities | null;
  return capabilities?.capabilities[module];
};

/**
 * True when this caller sees only the vulnerabilities assigned to them. Both the list and
 * `/vulnerabilities/stats` are filtered server-side on the same predicate, while the
 * `/r/v2/dashboard/*` aggregates are not — so a narrowed caller reads two different
 * numbers for the same thing and the difference has to be labelled.
 */
export const useAssignedOnlyVulnerabilities = (): boolean =>
  useAccessLevel('VULNERABILITY') === 'ASSIGNED_ONLY';

export type CapabilityCheck = 'any' | 'manage';

/**
 * Gate on the resolved capability for `module`: the active node's by default, or the
 * given node's (fetched on demand) when `nodeId` is passed. `'any'` means some
 * access beyond `NONE` (READ, ASSIGNED_ONLY, READ_OWN or MANAGE all count; this is a
 * visibility check, not a permission-level check); `'manage'` requires exactly MANAGE.
 * Returns `false` while capabilities are still loading, matching the fail-hidden
 * default the rest of this gating follows.
 */
export const useCapability = (
  module: string,
  check: CapabilityCheck = 'any',
  nodeId?: number
): boolean => {
  const active = useSelector(selectCapabilities) as SelfCapabilities | null;
  const node = useNodeCapabilities(nodeId);
  const capabilities = nodeId != null ? node : active;
  const level = capabilities?.capabilities[module];
  if (!level) return false;
  return check === 'manage' ? level === 'MANAGE' : level !== 'NONE';
};
